import { Alert, Card, Checkbox, Divider, InputNumber, Popover, Space, Tooltip, Typography } from "antd"
import { Action, calculateTotalsForPaymentGroups, findUnlinkedCostCodes, ProposalCreationParameters, setEstimateSalesTax, setIncludePricingType, setRedirectToDownPaymentCheckoutOnApproval } from "../proposalsModel"
import VSpace from "../../common/VSpace"
import DownPayment from "../DownPayment"
import { InfoCircleOutlined } from "@ant-design/icons"
import PaymentSchedule from "../PaymentSchedule"
import InvoiceRateSelector from "./InvoiceRateSelector"
import RadioOption from "./RadioOption"
import { PricingTypes } from "../../../common/constants"
import SynkedUPPaymentsBanner from "../../invoicing/SynkedUPPaymentsBanner"
import useFeatureFlag from "../../../hooks/useFeatureFlag"

type Props = {
  model: ProposalCreationParameters
  hideDownPayment?: boolean
  dispatch: (action: Action) => void
  onLinkCustomerToQbo: (shouldSave: boolean) => void,
  onLinkCostCodeToQbo: (costCodeId: number, shouldSave: boolean) => void,
}

const PaymentOptions = ({model, hideDownPayment, dispatch, onLinkCustomerToQbo, onLinkCostCodeToQbo }: Props) => {
  const { lineItems, downPayment, includePricingTypeInformation, redirectToDownPaymentCheckoutOnApproval, paymentScheduleOptions } = model
  const isQuickBooks = downPayment.invoicingSystem === 'quickBooks'
  const estimateSalesTaxDisabled = isQuickBooks && downPayment.isCustomerLinkedToQbo !== true 
  const totalsForPaymentGroups = calculateTotalsForPaymentGroups(model.lineItems)
  const includesTimeAndMaterialsOrFlatRate = lineItems.some(x => x.enabled && (x.pricingType ===  PricingTypes.TimeAndMaterials || x.pricingType === PricingTypes.FlatRate))

  const redirectFeatureEnabled = useFeatureFlag('proposal-redirect-option')
  const showRedirectOption = redirectFeatureEnabled && downPayment.invoicingSystem === 'synkedUp'
  const redirectDisabled = downPayment.autoSubmitDownPaymentInvoice === true && paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption === 'progress-based'

  return <div>
    <VSpace>
      <SynkedUPPaymentsBanner />
      <Typography.Title level={5} style={{ margin: 0 }}>Payment Options</Typography.Title>
      <Space>
        <Checkbox
          defaultChecked={model.estimateSalesTax}
          onChange={e => dispatch(setEstimateSalesTax(e.target.checked))}
          disabled={estimateSalesTaxDisabled}
          data-testid="estimateSalesTax">
          Estimate sales tax for this proposal
        </Checkbox>
        {isQuickBooks && <Popover content={<div style={{ width: "350px" }}>
            Sales tax settings in your QuickBooks Online account must be set up and working to successfully show estimated sales tax on your proposals.
            If we cannot create the estimate in QuickBooks for any reason, the estimate will not include estimated sales tax.
          </div>}>
          <InfoCircleOutlined />
        </Popover>}
        {estimateSalesTaxDisabled &&
          <Alert type="warning" showIcon message="Sales Tax Estimation Requires QuickBooks Link" description={
            <ul style={{ padding: 0 }}>
              <li>
                <Typography.Link onClick={() => onLinkCustomerToQbo(false)}>Link Customer</Typography.Link>
              </li>
            </ul>
          } />
        }
      </Space>
      <div>
        <Checkbox
          defaultChecked={includePricingTypeInformation}
          onChange={e => dispatch(setIncludePricingType(e.target.checked))}
          data-testid="includePricingTypeInformation"
        >
          Include Pricing Type Information in Customer Proposal Info
        </Checkbox>
      </div>
      {showRedirectOption && <div>
        <Tooltip title={redirectDisabled ? "Not available when auto-submitting a down-payment invoice." : undefined}>
          <Checkbox
            defaultChecked={redirectToDownPaymentCheckoutOnApproval}
            onChange={e => dispatch(setRedirectToDownPaymentCheckoutOnApproval(e.target.checked))}
            disabled={redirectDisabled}
            data-testid="redirectToDownPaymentCheckoutOnApproval"
          >
            Redirect the customer to payment method collection when approved
          </Checkbox>
        </Tooltip>
      </div>}
    </VSpace>

    {totalsForPaymentGroups.quotedAndTimeAndMaterials > 0 && <>
      <Divider />
      <VSpace>
        <Typography.Title level={5} style={{ margin: 0 }}>Payment Schedule for Quoted and Time & Materials</Typography.Title>

        Select a payment schedule:

        <RadioOption
          label="Progress-based"
          description="Pay by completion milestone ie: 20% upfront, 80% on completion"
          checked={paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption === 'progress-based'}
          onClick={() => dispatch({ type: 'selectQuotedAndTimeAndMaterialsPaymentOption', value: 'progress-based' })}
        />
        <RadioOption
          label="Interval-based"
          description="Equally distribute total across your chosen set of payments"
          checked={paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption === 'interval-based'}
          onClick={() => dispatch({ type: 'selectQuotedAndTimeAndMaterialsPaymentOption', value: 'interval-based' })}
        />
        
        { paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption === 'progress-based' && <VSpace>
          {!hideDownPayment && <Card style={{backgroundColor: '#f9f9f9'}}>
            <DownPayment
              downPayment={{...downPayment, costCodesUnlinkedToQbo: findUnlinkedCostCodes(lineItems)}}
              lineItems={lineItems}
              onLinkCustomerToQbo={onLinkCustomerToQbo}
              onLinkCostCodeToQbo={onLinkCostCodeToQbo}
              data-testid="autoSubmitDownPaymentInvoice"
              dispatch={dispatch}
              includesTimeAndMaterialsOrFlatRate={includesTimeAndMaterialsOrFlatRate}
            />
          </Card>}
          <PaymentSchedule
            paymentSchedule={paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased}
            total={totalsForPaymentGroups.quotedAndTimeAndMaterials}
            dispatch={dispatch}
          />
        </VSpace>}

        { paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption === 'interval-based' && <VSpace>
          <Card style={{backgroundColor: '#f9f9f9'}}>
            <VSpace>
              <Space>
                Total number of payments
                <InputNumber
                  value={paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased.totalPayments}
                  min={1}
                  max={100}
                  onChange={value => dispatch({type: 'updateIntervalBasedTotalPayments', value: value ?? 1})}
                />
              </Space>
              <InvoiceRateSelector
                recurringPayment={paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased.recurring}
                totalAmount={totalsForPaymentGroups.quotedAndTimeAndMaterials}
                totalPayments={paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased.totalPayments}
                onChange={value => dispatch({type: 'updateIntervalBasedRecurringPayment', value})}
                showEstimated={includesTimeAndMaterialsOrFlatRate}
              />
            </VSpace>
          </Card>
        </VSpace>}
      </VSpace>
    </>}

    {totalsForPaymentGroups.perVisit > 0 && <>
      <Divider />
      <VSpace>
        <Typography.Title level={5} style={{ margin: 0 }}>Payment Schedule for Per Visit</Typography.Title>

        <InvoiceRateSelector
          recurringPayment={paymentScheduleOptions.perVisit.recurring}
          totalAmount={totalsForPaymentGroups.perVisit}
          onChange={value => dispatch({type: 'updatePerVisitRecurringPayment', value})}
        />
      </VSpace>
    </>}
  </div>
}

export default PaymentOptions

